import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  Ref,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useApp } from '../../../../context/AppContext';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  SearchExpressionInputModel,
  UserDocument,
  UserDocumentModel,
} from '../../../../types/UserDocument';
import { SelectedOptions } from './types';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getSearchResults } from '../../../../services/SearchService';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { FilterKeys } from './filters/filters';
import { toPartialMatchSearchText } from './searchQueryUtils';

interface SearchContextType {
  searchExpression: SearchExpressionInputModel;
  setSearchExpression: React.Dispatch<
    React.SetStateAction<SearchExpressionInputModel>
  >;
  selectedOptions: SelectedOptions[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions[]>>;
  allUsers: UserDocument[] | undefined;
  searchResults: UserDocumentModel | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  sentryRef: Ref<any>;
  onClickOption: (search: string) => void;
  onSetFilterExpression: (expression: string) => void;
  filtersOpen: boolean;
  setFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;
  languageFilters: Set<string>;
  setLanguageFilters: React.Dispatch<React.SetStateAction<Set<string>>>;
  ratingFilters: Set<number>;
  setFeeFilters: React.Dispatch<
    React.SetStateAction<{ min: number; max: number } | undefined>
  >;
  setCategoryFilters: React.Dispatch<React.SetStateAction<Set<string>>>;
  getFilterQuery: (ignoreFacet?: FilterKeys) => string;
  feeFilters: { min: number; max: number } | undefined;
  categoryFilters: Set<string>;
  setRatingFilters: React.Dispatch<React.SetStateAction<Set<number>>>;
  resetFilters: () => void;
  filterCount?: number;
}

interface SearchContextProps {
  children: ReactNode;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<SearchContextProps> = ({ children }) => {
  const { token } = useApp();
  const { state } = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [languageFilters, setLanguageFilters] = useState<Set<string>>(
    new Set()
  );
  const [ratingFilters, setRatingFilters] = useState<Set<number>>(new Set());
  const [feeFilters, setFeeFilters] = useState<
    { min: number; max: number } | undefined
  >(undefined);
  const [categoryFilters, setCategoryFilters] = useState<Set<string>>(
    new Set()
  );
  const initialFiltersRef = useRef<{
    languageFilters: Set<string>;
    ratingFilters: Set<number>;
    feeFilters: { min: number; max: number } | undefined;
    categoryFilters: Set<string>;
  } | null>(null);
  const [searchExpression, setSearchExpression] =
    useState<SearchExpressionInputModel>({
      searchText: state?.searchText ?? '*',
      filterExpression: 'enabled eq true',
      skip: 0,
      size: 10,
    });

  const resetFilters = () => {
    const initialFilters = initialFiltersRef.current;

    setLanguageFilters(
      initialFilters
        ? new Set(initialFilters.languageFilters)
        : new Set()
    );
    setRatingFilters(
      initialFilters
        ? new Set(initialFilters.ratingFilters)
        : new Set()
    );
    setFeeFilters(initialFilters?.feeFilters);
    setCategoryFilters(
      initialFilters
        ? new Set(initialFilters.categoryFilters)
        : new Set()
    );
  };

  const getFilterQuery = (ignoreFacet?: FilterKeys) => {
    let filterExpressions: string[] = ['enabled eq true'];

    if (ignoreFacet !== FilterKeys.LANGUAGES && languageFilters.size > 0) {
      filterExpressions.push(
        `(${Array.from(languageFilters)
          .map(
            (lang) => `search.ismatch('${lang}', 'languages', 'simple', 'all')`
          )
          .join(' or ')})`
      );
    }

    if (ignoreFacet !== FilterKeys.FEEDBACK && ratingFilters.size > 0) {
      filterExpressions.push(
        `(${Array.from(ratingFilters)
          .map((rating) => `${FilterKeys.FEEDBACK} eq ${rating}`)
          .join(' or ')})`
      );
    }

    if (ignoreFacet !== FilterKeys.RATE && feeFilters) {
      filterExpressions.push(
        `(rate ge ${feeFilters.min} and rate le ${feeFilters.max})`
      );
    }

    if (ignoreFacet !== FilterKeys.CATEGORIES && categoryFilters.size > 0) {
      filterExpressions.push(
        `(${Array.from(categoryFilters)
          .map(
            (cat) => `search.ismatch('${cat}', 'categories', 'simple', 'all')`
          )
          .join(' or ')})`
      );
    }

    const combinedExpression = filterExpressions.join(' and ');

    return combinedExpression;
  };

  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions[]>([]);

  useEffect(() => {
    setSearchExpression((prev) => ({
      ...prev,
      filterExpression: getFilterQuery(),
      skip: 0,
    }));
  }, [ratingFilters, languageFilters, feeFilters, categoryFilters]);

  const filterCount = useMemo(() => {
    let count = 0;
    if (languageFilters.size > 0) count += languageFilters.size;
    if (ratingFilters.size > 0) count += ratingFilters.size;
    if (feeFilters) count += 1;
    if (categoryFilters.size > 0) count += categoryFilters.size;
    return count;
  }, [languageFilters, ratingFilters, feeFilters, categoryFilters]);

  const normalizedSearchText = useMemo(
    () => toPartialMatchSearchText(searchExpression.searchText),
    [searchExpression.searchText]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: [
      'facets',
      normalizedSearchText,
      searchExpression.filterExpression,
    ],
    queryFn: ({ pageParam = 0 }) =>
      getSearchResults(
        {
          ...searchExpression,
          searchText: normalizedSearchText,
          skip: pageParam,
        },
        token
      ),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.users.length < searchExpression.size) return undefined;
      return allPages.length * searchExpression.size;
    },
    enabled: !!token && !!normalizedSearchText,
    initialPageParam: 0,
  });

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: isError,
    rootMargin: '0px 0px 400px 0px',
  });

  // Flatten all pages into a single array of users, filtering to only enabled profiles
  const allUsers = (data?.pages.flatMap((page) => page.users) ?? []).filter((u) => u.enabled);
  const searchResults = data?.pages[0]; // Use first page for facets

  const onClickOption = (search: string) => {
    setSearchExpression({
      ...searchExpression,
      searchText: search,
      skip: 0,
    });
  };

  const onSetFilterExpression = (expression: string) => {
    setSearchExpression({
      ...searchExpression,
      filterExpression: expression,
      skip: 0,
    });
  };

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  const areSetsEqual = <T,>(a: Set<T>, b: Set<T>) =>
    a.size === b.size && [...a].every((v) => b.has(v));

  useEffect(() => {
    const fromParam = (name: string) =>
      new Set(
        (searchParams.get(name) ?? '')
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );

    const nextLanguages = fromParam(FilterKeys.LANGUAGES);
    if (!areSetsEqual(languageFilters, nextLanguages))
      setLanguageFilters(nextLanguages);

    const nextRatings = new Set(
      [...fromParam(FilterKeys.RATE)]
        .map((v) => Number(v))
        .filter((v) => !isNaN(v))
    );
    if (!areSetsEqual(ratingFilters, nextRatings))
      setRatingFilters(nextRatings);

    const nextCategories = fromParam(FilterKeys.CATEGORIES);
    if (!areSetsEqual(categoryFilters, nextCategories))
      setCategoryFilters(nextCategories);

    const feeMin = searchParams.get(FilterKeys.MIN_RATE);
    const feeMax = searchParams.get(FilterKeys.MAX_RATE);
    const parsedFee =
      feeMin !== null && feeMax !== null && !isNaN(+feeMin) && !isNaN(+feeMax)
        ? { min: Number(feeMin), max: Number(feeMax) }
        : undefined;
    const isFeeSame =
      (parsedFee === undefined && feeFilters === undefined) ||
      (parsedFee &&
        feeFilters &&
        parsedFee.min === feeFilters.min &&
        parsedFee.max === feeFilters.max);
    if (!isFeeSame) setFeeFilters(parsedFee);

    if (!initialFiltersRef.current) {
      initialFiltersRef.current = {
        languageFilters: nextLanguages,
        ratingFilters: nextRatings,
        feeFilters: parsedFee,
        categoryFilters: nextCategories,
      };
    }

    setFiltersHydrated(true);
  }, [searchParams]);

  useEffect(() => {
    if (!filtersHydrated) return;

    const next = new URLSearchParams(searchParams);

    if (languageFilters.size)
      next.set(FilterKeys.LANGUAGES, Array.from(languageFilters).join(','));
    else next.delete(FilterKeys.LANGUAGES);

    if (ratingFilters.size)
      next.set(FilterKeys.RATE, Array.from(ratingFilters).join(','));
    else next.delete(FilterKeys.RATE);

    if (categoryFilters.size)
      next.set(FilterKeys.CATEGORIES, Array.from(categoryFilters).join(','));
    else next.delete(FilterKeys.CATEGORIES);
    if (feeFilters) {
      next.set(FilterKeys.MIN_RATE, String(feeFilters.min));
      next.set(FilterKeys.MAX_RATE, String(feeFilters.max));
    } else {
      next.delete(FilterKeys.MIN_RATE);
      next.delete(FilterKeys.MAX_RATE);
    }

    if (next.toString() !== searchParams.toString())
      setSearchParams(next, { replace: true });
  }, [
    languageFilters,
    ratingFilters,
    categoryFilters,
    feeFilters,
    filtersHydrated,
    searchParams,
  ]);

  return (
    <SearchContext.Provider
      value={{
        searchExpression,
        setSearchExpression,
        selectedOptions,
        setSelectedOptions,
        allUsers,
        searchResults,
        isLoading,
        isError,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        sentryRef,
        onClickOption,
        onSetFilterExpression,
        filtersOpen,
        setFiltersOpen,
        languageFilters,
        setLanguageFilters,
        ratingFilters,
        setFeeFilters,
        setCategoryFilters,
        getFilterQuery,
        setRatingFilters,
        categoryFilters,
        feeFilters,
        resetFilters,
        filterCount,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);

  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }

  return context;
};
